import { BridgeComponent } from "@hotwired/strada"

export default class extends BridgeComponent {
  static component = "appearance"

  connect() {
    super.connect()
    this.syncTheme()

    this.themeObserver = new MutationObserver(() => this.syncTheme())
    // `data-bridge-components` : posé quand l'app branche le pont. Au chargement complet d'une
    // page (démarrage à froid, mode sombre basculé dans le système), le contrôleur se connecte
    // avant : sans ce rappel, les barres natives gardaient les couleurs du thème précédent.
    this.themeObserver.observe(document.documentElement, {
      attributes: true,
      attributeFilter: ["data-bs-theme", "data-bridge-components"]
    })
    this.themeObserver.observe(document.body, {
      attributes: true,
      attributeFilter: ["data-bs-theme"]
    })

    this.boundThemeChanged = () => this.syncTheme()
    document.addEventListener("depozen:theme-changed", this.boundThemeChanged)
  }

  disconnect() {
    this.themeObserver?.disconnect()
    document.removeEventListener("depozen:theme-changed", this.boundThemeChanged)
  }

  syncTheme() {
    if (!this.enabled) return

    const styles = getComputedStyle(document.documentElement)
    const backgroundColor = this.readBackgroundColor(styles)
    const colorScheme = this.readColorScheme()

    this.updateThemeColorMeta(backgroundColor, colorScheme)

    this.send("connect", {
      primaryColor: this.readPrimaryColor(styles),
      backgroundColor,
      bodyFont: this.readFontFamily(styles, "--bs-body-font-family"),
      headingsFont: this.readFontFamily(styles, "--headings-font")
    })
  }

  readColorScheme() {
    return (
      document.body.getAttribute("data-bs-theme") ||
      document.documentElement.getAttribute("data-bs-theme") ||
      "light"
    )
  }

  readBackgroundColor(styles) {
    const candidates = [
      styles.getPropertyValue("--bs-body-bg").trim(),
      styles.backgroundColor.trim()
    ]

    for (const value of candidates) {
      const hex = this.colorToHex(value)
      if (hex) return hex
    }

    return this.readColorScheme() === "dark" ? "#495057" : "#FFFFFF"
  }

  // Toujours `#rrggbb` : les apps ne lisent pas la forme courte de Bootstrap (`--bs-body-bg: #fff`).
  // Au retour du thème sombre au thème clair, Android gardait ses barres sombres.
  colorToHex(value) {
    if (!value) return null

    const hex = value.match(/^#([0-9a-f]{3,8})$/i)?.[1]
    if (hex?.length === 3 || hex?.length === 4) {
      return `#${[...hex.slice(0, 3)].map((digit) => digit + digit).join("")}`
    }
    if (hex?.length === 6 || hex?.length === 8) return `#${hex.slice(0, 6)}`
    if (hex) return null

    const rgbMatch = value.match(/^rgba?\((\d+),\s*(\d+),\s*(\d+)/)
    if (rgbMatch) {
      const [, red, green, blue] = rgbMatch
      return `#${[red, green, blue].map((part) => Number(part).toString(16).padStart(2, "0")).join("")}`
    }

    return null
  }

  updateThemeColorMeta(backgroundColor, colorScheme) {
    const meta =
      document.querySelector('meta[name="theme-color"]') ||
      Object.assign(document.createElement("meta"), { name: "theme-color" })

    meta.content = backgroundColor
    if (!meta.parentElement) document.head.appendChild(meta)

    const appleMeta = document.querySelector('meta[name="apple-mobile-web-app-status-bar-style"]')
    if (appleMeta) {
      appleMeta.content = colorScheme === "dark" ? "black-translucent" : "default"
    }
  }

  readPrimaryColor(styles) {
    const candidates = [
      styles.getPropertyValue("--primary").trim(),
      styles.getPropertyValue("--bs-primary").trim()
    ]

    for (const value of candidates) {
      const hex = this.colorToHex(value)
      if (hex) return hex
    }

    return "#29A37A"
  }

  readFontFamily(styles, variable) {
    const value = styles.getPropertyValue(variable).trim()
    if (!value) return null

    return value
      .split(",")[0]
      .trim()
      .replace(/^['"]|['"]$/g, "")
  }
}
